import React, { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  DatePicker,
  message,
  InputNumber,
  Radio,
  Upload,
  Space,
} from 'antd';
import type { RangePickerProps } from 'antd/es/date-picker';
import { useNavigate } from 'react-router-dom';
import { createVoteContest } from '@/api/voteActivity';
import type { CreateVoteContestParams } from '@/api/voteActivity';
import dayjs from 'dayjs';

const VoteActivityCreatePage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [ruleType, setRuleType] = useState<number>(1);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (submitting) return;
    
    try {
      setSubmitting(true);
      const values = await form.validateFields();
      
      // 验证权重值的合法性
      const weightFields = ['voteWeight', 'likeWeight', 'commentWeight', 'collectWeight', 'artlistWeight'];
      weightFields.forEach(field => {
        if (values[field] && !/^\d+(\.\d+)?$/.test(values[field])) {
          throw new Error(`${field} 必须是有效的数字`);
        }
      });

      const params: CreateVoteContestParams = {
        ...values,
        submitBeginTime: values.submitTime?.[0]?.format('YYYY-MM-DD HH:mm'),
        submitEndTime: values.submitTime?.[1]?.format('YYYY-MM-DD HH:mm'),
      };
      delete params.submitTime;

      const response = await createVoteContest(params);
      if (response.success) {
        message.success('创建成功');
        navigate('/vote-activity/submissions');
      } else {
        message.error(response.message || '创建失败');
      }
    } catch (error: any) {
      if (error.errorFields) {
        message.error('请检查表单填写是否正确');
      } else {
        message.error(error.message || '创建失败');
      }
      console.error('表单验证失败:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDate: RangePickerProps['disabledDate'] = (current) => {
    return current && current < dayjs().startOf('day');
  };

  // 验证数字权重的规则
  const validateWeight = (_: any, value: string) => {
    if (!value) {
      return Promise.resolve();
    }
    if (!/^\d+(\.\d+)?$/.test(value)) {
      return Promise.reject('请输入有效的数字');
    }
    return Promise.resolve();
  };

  return (
    <Card title="创建投票活动">
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 800, margin: '0 auto' }}
        initialValues={{
          ruleType: 1,
          voteWeight: '1',
          likeWeight: '1',
          commentWeight: '1',
          collectWeight: '1',
          artlistWeight: '1',
        }}
      >
        <Form.Item
          name="title"
          label="主标题"
          rules={[
            { required: true, message: '请输入主标题' },
            { max: 50, message: '主标题最多50个字符' }
          ]}
        >
          <Input placeholder="请输入主标题" />
        </Form.Item>

        <Form.Item
          name="subtitle"
          label="副标题"
          rules={[{ max: 100, message: '副标题最多100个字符' }]}
        >
          <Input placeholder="请输入副标题" />
        </Form.Item>

        <Form.Item
          name="contestImage"
          label="活动图片"
          rules={[{ required: true, message: '请输入活动图片URL' }]}
        >
          <Input placeholder="请输入图片URL" />
        </Form.Item>

        <Form.Item
          name="submitTime"
          label="投稿时间"
          rules={[{ required: true, message: '请选择投稿时间' }]}
        >
          <DatePicker.RangePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            style={{ width: '100%' }}
            disabledDate={disabledDate}
            placeholder={['开始时间', '结束时间']}
          />
        </Form.Item>

        <Space size="large" style={{ display: 'flex' }}>
          <Form.Item
            name="prizeNum"
            label="奖励人数"
            rules={[
              { required: true, message: '请输入奖励人数' },
              { type: 'number', min: 1, message: '奖励人数必须大于0' }
            ]}
          >
            <InputNumber min={1} placeholder="奖励人数" />
          </Form.Item>

          <Form.Item
            name="prizeQuota"
            label="每人奖励额度"
            rules={[
              { required: true, message: '请输入每人奖励额度' },
              { type: 'number', min: 1, message: '奖励额度必须大于0' }
            ]}
          >
            <InputNumber min={1} placeholder="奖励额度" />
          </Form.Item>
        </Space>

        <Form.Item
          name="ruleType"
          label="规则类型"
          rules={[{ required: true, message: '请选择规则类型' }]}
        >
          <Radio.Group onChange={(e) => setRuleType(e.target.value)}>
            <Radio value={1}>文字</Radio>
            <Radio value={2}>图片</Radio>
            <Radio value={3}>链接</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="ruleVal"
          label="规则内容"
          rules={[{ required: true, message: '请输入规则内容' }]}
        >
          {ruleType === 1 ? (
            <Input.TextArea rows={4} placeholder="请输入规则文字内容" />
          ) : ruleType === 2 ? (
            <Input placeholder="请输入图片URL" />
          ) : (
            <Input placeholder="请输入链接URL" />
          )}
        </Form.Item>

        <Card title="评分权重设置" size="small" style={{ marginBottom: 24 }}>
          <Space size="large" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
            <Form.Item
              name="voteWeight"
              label="投票权重"
              rules={[{ validator: validateWeight }]}
            >
              <Input placeholder="投票权重" style={{ width: 120 }} />
            </Form.Item>

            <Form.Item
              name="likeWeight"
              label="点赞权重"
              rules={[{ validator: validateWeight }]}
            >
              <Input placeholder="点赞权重" style={{ width: 120 }} />
            </Form.Item>

            <Form.Item
              name="commentWeight"
              label="评论权重"
              rules={[{ validator: validateWeight }]}
            >
              <Input placeholder="评论权重" style={{ width: 120 }} />
            </Form.Item>

            <Form.Item
              name="collectWeight"
              label="收藏权重"
              rules={[{ validator: validateWeight }]}
            >
              <Input placeholder="收藏权重" style={{ width: 120 }} />
            </Form.Item>

            <Form.Item
              name="artlistWeight"
              label="Artlist引用权重"
              rules={[{ validator: validateWeight }]}
            >
              <Input placeholder="Artlist引用权重" style={{ width: 120 }} />
            </Form.Item>
          </Space>
        </Card>

        <Form.Item>
          <Button 
            type="primary" 
            onClick={handleSubmit} 
            loading={submitting}
            block
          >
            创建活动
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default VoteActivityCreatePage;
